/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Quick Image Operations (4K Upscale & Background Removal) Complete Studio View (Task 10.4.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef } from 'react';
import {
  Wand2,
  Sparkles,
  Scissors,
  UploadCloud,
  Image as ImageIcon,
  Download,
  Check,
  AlertCircle,
  Maximize2,
  SplitSquareVertical,
  Layers,
  Palette,
  X,
  RefreshCw,
  Sliders,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  useImageOpsStore,
  UPSCALE_2X_COST,
  UPSCALE_4X_COST,
  REMOVE_BG_COST,
} from '../stores/useImageOpsStore';
import { useImageStudioStore } from '../stores/useImageStudioStore';
import { useInpaintingStore } from '../stores/useInpaintingStore';
import { BeforeAfterSlider } from '../components/media/BeforeAfterSlider';

// Curated demo assets for immediate 1-click testing
const OPS_SAMPLE_PRESETS = [
  {
    id: 'sample-portrait',
    label: 'Studio Portrait Photography',
    tag: 'Best for 4K Upscale & Cutout',
    url: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=1024&q=80',
  },
  {
    id: 'sample-product',
    label: 'Minimalist Sneaker Product',
    tag: 'E-Commerce Background Removal',
    url: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1024&q=80',
  },
  {
    id: 'sample-architecture',
    label: 'Modern Minimalist Architecture',
    tag: 'High-Frequency 4K Texture',
    url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1024&q=80',
  },
];

interface ImageOpsStudioViewProps {
  onNavigateToInpaint?: () => void;
  onNavigateToGallery?: () => void;
}

export const ImageOpsStudioView: React.FC<ImageOpsStudioViewProps> = ({
  onNavigateToInpaint,
  onNavigateToGallery,
}) => {
  const { t } = useTranslation();

  const {
    sourceImage,
    processedImage,
    activeTab,
    viewMode,
    scaleFactor,
    creativity,
    bgModel,
    isProcessing,
    jobProgress,
    jobStage,
    errorMessage,
    setSourceImage,
    setActiveTab,
    setViewMode,
    setScaleFactor,
    setCreativity,
    setBgModel,
    executeUpscale,
    executeRemoveBg,
    applyResultAsSource,
  } = useImageOpsStore();

  const { historyImages } = useImageStudioStore();

  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // File Upload Handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload a valid image file (PNG, JPG, WebP)');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setSourceImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Download Handler
  const handleDownload = (imageUrl: string, filename = 'sofia-enhanced-image.png') => {
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Save to Media Gallery
  const handleSaveToGallery = () => {
    if (!processedImage) return;

    const imageStudio = useImageStudioStore.getState();
    const cost =
      activeTab === 'upscale'
        ? scaleFactor === '4x'
          ? UPSCALE_4X_COST
          : UPSCALE_2X_COST
        : REMOVE_BG_COST;

    const newGalleryItem = {
      id: `ops_${Date.now()}`,
      url: processedImage,
      prompt: activeTab === 'upscale' ? `Super-Resolution ${scaleFactor}` : 'Background Removed',
      model: activeTab === 'upscale' ? 'real-esrgan' : bgModel,
      modelName: activeTab === 'upscale' ? `Upscale ${scaleFactor}` : 'BiRefNet Cutout',
      aspectRatio: '1:1',
      width: scaleFactor === '4x' ? 2048 : 1024,
      height: scaleFactor === '4x' ? 2048 : 1024,
      style: activeTab === 'upscale' ? '4K Super-Resolution' : 'Transparent PNG',
      seed: Math.floor(Math.random() * 1000000),
      creditsUsed: cost,
      createdAt: new Date().toISOString(),
      isFavorite: false,
    };

    imageStudio.historyImages.unshift(newGalleryItem);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  // Send to Inpainting Canvas
  const handleSendToInpaint = () => {
    const targetImage = processedImage || sourceImage;
    if (!targetImage) return;

    useInpaintingStore.getState().setSourceImage(targetImage);
    onNavigateToInpaint?.();
  };

  const currentUpscaleCost = scaleFactor === '4x' ? UPSCALE_4X_COST : UPSCALE_2X_COST;

  return (
    <div className="space-y-6">
      {/* 1. Header & Source Selector Toolbar */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="font-heading font-bold text-xl text-foreground">
              {t('imageOps.title')}
            </h2>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-primary/10 text-primary uppercase tracking-wider">
              {activeTab === 'upscale'
                ? scaleFactor === '4x'
                  ? t('imageOps.scale4xCost')
                  : t('imageOps.scale2xCost')
                : t('imageOps.removeBgCost')}
            </span>
          </div>
          <p className="text-xs text-foreground-muted leading-relaxed">
            {t('imageOps.subtitle')}
          </p>
        </div>

        {/* Source Action Buttons */}
        <div className="flex flex-wrap items-center gap-2.5">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFileUpload}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-3.5 py-2 rounded-xl bg-surface-elevated border border-surface-border hover:border-primary/50 text-foreground text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
          >
            <UploadCloud className="w-4 h-4 text-primary" />
            <span>{t('imageOps.uploadSource')}</span>
          </button>

          <button
            type="button"
            onClick={() => (onNavigateToGallery ? onNavigateToGallery() : setIsGalleryModalOpen(true))}
            className="px-3.5 py-2 rounded-xl bg-surface-elevated border border-surface-border hover:border-primary/50 text-foreground text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
          >
            <ImageIcon className="w-4 h-4 text-accent" />
            <span>{t('imageOps.chooseFromGallery')}</span>
          </button>
        </div>
      </div>

      {/* 2. Mode Tab Navigation Switcher */}
      <div className="flex items-center gap-2 p-1.5 rounded-2xl bg-surface border border-surface-border shadow-sm w-fit">
        <button
          type="button"
          onClick={() => setActiveTab('upscale')}
          className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
            activeTab === 'upscale'
              ? 'bg-primary text-primary-fg shadow-sm'
              : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
          }`}
        >
          <Maximize2 className="w-4 h-4" />
          <span>{t('imageOps.upscaleTab')}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('remove_bg')}
          className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
            activeTab === 'remove_bg'
              ? 'bg-primary text-primary-fg shadow-sm'
              : 'text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
          }`}
        >
          <Scissors className="w-4 h-4" />
          <span>{t('imageOps.removeBgTab')}</span>
        </button>
      </div>

      {/* 3. Main Studio Workspace (Left: Interactive Canvas & Slider, Right: Controls) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Canvas Viewport (8 Cols) */}
        <div className="lg:col-span-8 space-y-4">
          <div className="relative w-full h-[540px] sm:h-[620px] rounded-2xl bg-surface-elevated/40 border border-surface-border overflow-hidden flex items-center justify-center shadow-glass">
            {/* Background Grid Pattern */}
            <div
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(var(--color-foreground) 1px, transparent 1px)',
                backgroundSize: '16px 16px',
              }}
            />

            {sourceImage && processedImage ? (
              viewMode === 'slider' ? (
                /* Interactive Before / After Split Comparison Slider */
                <BeforeAfterSlider
                  beforeImage={sourceImage}
                  afterImage={processedImage}
                  beforeLabel={t('imageOps.originalImage')}
                  afterLabel={t('imageOps.processedImage')}
                  isTransparentAfter={activeTab === 'remove_bg'}
                  className="w-full h-full"
                />
              ) : (
                /* Side-by-Side Dual Viewport */
                <div className="grid grid-cols-2 gap-4 w-full h-full p-4">
                  <div className="relative rounded-xl overflow-hidden border border-surface-border bg-black/20 flex items-center justify-center">
                    <img
                      src={sourceImage}
                      alt="Source"
                      className="max-w-full max-h-full object-contain"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold bg-surface/80 text-foreground border border-surface-border backdrop-blur-md">
                      {t('imageOps.originalImage')}
                    </span>
                  </div>

                  <div className="relative rounded-xl overflow-hidden border border-surface-border bg-black/20 flex items-center justify-center">
                    {activeTab === 'remove_bg' && (
                      <div
                        className="absolute inset-0 pointer-events-none opacity-40"
                        style={{
                          backgroundImage:
                            'linear-gradient(45deg, #888 25%, transparent 25%), linear-gradient(-45deg, #888 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #888 75%), linear-gradient(-45deg, transparent 75%, #888 75%)',
                          backgroundSize: '16px 16px',
                        }}
                      />
                    )}
                    <img
                      src={processedImage}
                      alt="Processed"
                      className="max-w-full max-h-full object-contain relative z-10"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold bg-primary text-primary-fg shadow-sm backdrop-blur-md z-20">
                      {t('imageOps.processedImage')}
                    </span>
                  </div>
                </div>
              )
            ) : sourceImage ? (
              /* Single Source Image Preview */
              <div className="relative w-full h-full p-4 flex items-center justify-center">
                <img
                  src={sourceImage}
                  alt="Source"
                  className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
                />
                <span className="absolute top-6 left-6 px-3 py-1 rounded-full text-[11px] font-bold bg-surface/90 text-foreground border border-surface-border backdrop-blur-md">
                  {t('imageOps.originalImage')}
                </span>
              </div>
            ) : (
              /* Empty Drop Zone State */
              <div className="flex flex-col items-center justify-center p-8 text-center space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-surface-border/40 flex items-center justify-center text-foreground-muted">
                  <Wand2 className="w-8 h-8 opacity-60 text-primary" />
                </div>
                <div className="space-y-1 max-w-sm">
                  <h4 className="font-heading font-semibold text-base text-foreground">
                    {t('imageOps.noImageSelected')}
                  </h4>
                  <p className="text-xs text-foreground-muted leading-relaxed">
                    {t('imageOps.noImageSelectedDesc')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Toolbar below Viewport */}
          {sourceImage && (
            <div className="p-3.5 rounded-2xl bg-surface border border-surface-border shadow-sm flex flex-wrap items-center justify-between gap-3">
              {/* Left: View Mode Toggle */}
              {processedImage && (
                <div className="flex items-center gap-1 p-1 rounded-xl bg-surface-elevated/80 border border-surface-border text-xs">
                  <button
                    type="button"
                    onClick={() => setViewMode('slider')}
                    className={`px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5 transition-all ${
                      viewMode === 'slider'
                        ? 'bg-primary text-primary-fg shadow-sm'
                        : 'text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <SplitSquareVertical className="w-3.5 h-3.5" />
                    <span>{t('imageOps.viewModeSlider')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setViewMode('side-by-side')}
                    className={`px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5 transition-all ${
                      viewMode === 'side-by-side'
                        ? 'bg-primary text-primary-fg shadow-sm'
                        : 'text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>{t('imageOps.viewModeSideBySide')}</span>
                  </button>
                </div>
              )}

              {/* Right: Export & Transfer Actions */}
              <div className="flex flex-wrap items-center gap-2 ml-auto">
                {processedImage && (
                  <>
                    <button
                      type="button"
                      onClick={applyResultAsSource}
                      className="px-3 py-2 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground font-semibold text-xs flex items-center gap-1.5 transition-colors"
                      title={t('imageOps.applyAsSource')}
                    >
                      <RefreshCw className="w-3.5 h-3.5 text-primary" />
                      <span>{t('imageOps.applyAsSource')}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDownload(processedImage)}
                      className="px-3 py-2 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground font-semibold text-xs flex items-center gap-1.5 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>{t('imageOps.downloadProcessed')}</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleSaveToGallery}
                      className="px-3 py-2 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground font-semibold text-xs flex items-center gap-1.5 transition-colors"
                    >
                      {saveSuccess ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-emerald-500">{t('common.saved')}</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-3.5 h-3.5 text-accent" />
                          <span>{t('imageOps.saveToGallery')}</span>
                        </>
                      )}
                    </button>
                  </>
                )}

                {onNavigateToInpaint && (
                  <button
                    type="button"
                    onClick={handleSendToInpaint}
                    className="px-3 py-2 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground font-semibold text-xs flex items-center gap-1.5 transition-colors"
                  >
                    <Palette className="w-3.5 h-3.5 text-rose-500" />
                    <span>{t('imageOps.sendToInpaint')}</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Settings & Execution Controls (4 Cols) */}
        <div className="lg:col-span-4 space-y-6">
          <div className="p-5 rounded-2xl bg-surface border border-surface-border shadow-sm space-y-5">
            <h3 className="font-heading font-bold text-sm text-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span>{t('imageOps.quickActionsTitle')}</span>
            </h3>

            {/* TAB 1: UPSCALE SETTINGS */}
            {activeTab === 'upscale' ? (
              <div className="space-y-4">
                {/* Scale Magnification Selection */}
                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-foreground-muted">
                    {t('imageOps.scaleFactor')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setScaleFactor('2x')}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        scaleFactor === '2x'
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-surface-border bg-surface-elevated/40 hover:border-surface-border/80'
                      }`}
                    >
                      <div className="font-bold text-xs text-foreground">2x HD</div>
                      <span className="text-[10px] text-primary font-semibold mt-1 block">
                        {t('imageOps.scale2xCost')}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setScaleFactor('4x')}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        scaleFactor === '4x'
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-surface-border bg-surface-elevated/40 hover:border-surface-border/80'
                      }`}
                    >
                      <div className="font-bold text-xs text-foreground">4x Ultra 4K</div>
                      <span className="text-[10px] text-primary font-semibold mt-1 block">
                        {t('imageOps.scale4xCost')}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Creativity Slider */}
                <div className="space-y-2 p-3.5 rounded-xl bg-surface-elevated/50 border border-surface-border">
                  <div className="flex items-center justify-between text-xs font-semibold">
                    <span className="text-foreground flex items-center gap-1.5">
                      <Sliders className="w-3.5 h-3.5 text-primary" />
                      <span>{t('imageOps.creativityLabel')}</span>
                    </span>
                    <span className="text-primary">{Math.round(creativity * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={creativity}
                    onChange={(e) => setCreativity(Number(e.target.value))}
                    className="w-full accent-primary cursor-pointer"
                  />
                  <p className="text-[11px] text-foreground-muted leading-relaxed">
                    {t('imageOps.creativityHint')}
                  </p>
                </div>

                {/* Execute Button */}
                <button
                  type="button"
                  disabled={isProcessing || !sourceImage}
                  onClick={() => void executeUpscale()}
                  className="w-full py-3 px-4 rounded-xl bg-primary text-primary-fg hover:opacity-90 font-bold text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-glow-primary/20"
                >
                  <Maximize2 className="w-4 h-4" />
                  <span>{t('imageOps.runUpscaleBtn')}</span>
                  <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-white/20">
                    {currentUpscaleCost} {t('common.credits')}
                  </span>
                </button>
              </div>
            ) : (
              /* TAB 2: BACKGROUND REMOVAL SETTINGS */
              <div className="space-y-4">
                {/* Matting Model Selection */}
                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-foreground-muted">
                    {t('imageOps.bgModelLabel')}
                  </label>
                  <div className="space-y-2">
                    <div
                      onClick={() => setBgModel('birefnet')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        bgModel === 'birefnet'
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-surface-border bg-surface-elevated/40 hover:border-surface-border/80'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-xs text-foreground">
                          {t('imageOps.bgModelBirefnet')}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-primary/10 text-primary">
                          Alpha Pro
                        </span>
                      </div>
                    </div>

                    <div
                      onClick={() => setBgModel('rmbg')}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        bgModel === 'rmbg'
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-surface-border bg-surface-elevated/40 hover:border-surface-border/80'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-xs text-foreground">
                          {t('imageOps.bgModelRmbg')}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-surface-border text-foreground-muted">
                          Fast
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transparency Notice */}
                <div className="p-3.5 rounded-xl bg-surface-elevated/50 border border-surface-border text-xs text-foreground-muted leading-relaxed space-y-1">
                  <div className="font-semibold text-foreground flex items-center gap-1.5">
                    <Scissors className="w-3.5 h-3.5 text-accent" />
                    <span>Transparent Alpha Channel</span>
                  </div>
                  <p className="text-[11px]">{t('imageOps.transparentNotice')}</p>
                </div>

                {/* Execute Button */}
                <button
                  type="button"
                  disabled={isProcessing || !sourceImage}
                  onClick={() => void executeRemoveBg()}
                  className="w-full py-3 px-4 rounded-xl bg-primary text-primary-fg hover:opacity-90 font-bold text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-glow-primary/20"
                >
                  <Scissors className="w-4 h-4" />
                  <span>{t('imageOps.runRemoveBgBtn')}</span>
                  <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-white/20">
                    {REMOVE_BG_COST} {t('common.credits')}
                  </span>
                </button>
              </div>
            )}

            {/* Error Message Display */}
            {errorMessage && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">{errorMessage}</span>
              </div>
            )}

            {/* Progress Bar when Generating */}
            {isProcessing && (
              <div className="space-y-2 p-3.5 rounded-xl bg-surface-elevated/60 border border-surface-border">
                <div className="flex items-center justify-between text-xs font-semibold">
                  <span className="text-foreground">{t('imageOps.processing')}</span>
                  <span className="text-primary">{jobProgress}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-surface-border/60 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300 rounded-full"
                    style={{ width: `${jobProgress}%` }}
                  />
                </div>
                <p className="text-[11px] text-foreground-muted">{jobStage}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 4. Choose from Gallery Modal */}
      {isGalleryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="relative w-full max-w-2xl rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-base text-foreground">
                  {t('imageOps.chooseFromGallery')}
                </h3>
                <p className="text-xs text-foreground-muted">
                  {t('imageOps.dragDropHint')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsGalleryModalOpen(false)}
                className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Demo Presets */}
            <div className="space-y-2">
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                Demo Presets
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {OPS_SAMPLE_PRESETS.map((sample) => (
                  <div
                    key={sample.id}
                    onClick={() => {
                      setSourceImage(sample.url);
                      setIsGalleryModalOpen(false);
                    }}
                    className="group cursor-pointer rounded-xl overflow-hidden border border-surface-border hover:border-primary transition-all bg-surface-elevated/40 flex flex-col justify-between"
                  >
                    <div className="aspect-video w-full overflow-hidden bg-black/20">
                      <img
                        src={sample.url}
                        alt={sample.label}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-2.5">
                      <span className="font-medium text-xs text-foreground block truncate">
                        {sample.label}
                      </span>
                      <span className="text-[10px] text-foreground-muted block mt-0.5">
                        {sample.tag}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Generated Gallery Images */}
            {historyImages.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-surface-border">
                <span className="text-xs font-semibold text-accent uppercase tracking-wider">
                  Workspace Gallery ({historyImages.length})
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-60 overflow-y-auto pr-1">
                  {historyImages.map((img) => (
                    <div
                      key={img.id}
                      onClick={() => {
                        setSourceImage(img.url);
                        setIsGalleryModalOpen(false);
                      }}
                      className="group cursor-pointer rounded-xl overflow-hidden border border-surface-border hover:border-primary transition-all aspect-square bg-black/20 relative"
                    >
                      <img
                        src={img.url}
                        alt={img.prompt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
